import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { COMPANIES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { buildCompanyRelationParams, resolveCompanyLogoUrl } from './companyApiHelpers';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const ViewCompany = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(COMPANIES.BY_ID(id), {
          params: buildCompanyRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setCompany(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load company');
        navigate('/companies');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !company) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  const logoSrc = resolveCompanyLogoUrl(company.logo);

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/companies')} sx={{ mb: 2 }}>
          Back to companies
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {company.name ?? 'Company'}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate(`/companies/edit/${id}`)}>
            Edit
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {row('Status', company.status?.name ?? company.status?.label ?? company.status_name)}
              {row(
                'Currency',
                company.currency?.name ?? company.currency?.code ?? company.currency_code
              )}
              {row('Name', company.name)}
              {row('Slug', company.slug)}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Logo
                </Typography>
                {logoSrc ? (
                  <Avatar src={logoSrc} variant="rounded" sx={{ width: 80, height: 80, mt: 1 }} />
                ) : (
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    —
                  </Typography>
                )}
              </Grid>
              {row('Email', company.email)}
              {row('Email forward', company.email_forward)}
              {row('Address', company.address)}
              {row('Invoice URL', company.invoice_url)}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewCompany;
